// src/components/UI/NotificationBell.jsx
//
// The bell is now a VIEW over the shared notification store — it holds no list
// of its own, so the badge, the page and any toast can never disagree.
//
// Behaviour that changed and why:
//   • the badge comes from the server's unread count, not from the length of
//     whatever page happened to be fetched (the old one was wrong above 50);
//   • there is no type whitelist — anything the backend registry knows about
//     renders, so a new module's notifications appear without a frontend edit;
//   • clicking navigates to the link the SERVER resolved and stored, which is
//     the same string the browser tray uses.

import React, { useState } from 'react';
import {
  Badge, Dropdown, List, Typography, Empty, Button, Space, Tooltip, Popconfirm,
  Segmented, Skeleton, theme,
} from 'antd';
import { BellOutlined, DeleteOutlined, CheckOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../modules/notifications/NotificationContext';
import { NotificationIcon, relativeTime, dayBucket, severityColor } from '../../modules/notifications/registry';

const { Text } = Typography;

const NotificationBell = () => {
  const {
    items, unread, loading, hasMore, streamStatus,
    refresh, loadMore, markRead, markAllRead, dismiss, dismissAll,
    openNotification, setBellOpen,
  } = useNotifications();

  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const visible = filter === 'unread' ? items.filter((n) => !n.read) : items;

  const handleClick = (item) => {
    if (!item.read) markRead(item.id);
    setOpen(false);
    setBellOpen(false);
    openNotification(item);
  };

  const onOpenChange = (next) => {
    setOpen(next);
    setBellOpen(next);
    if (next) refresh();
  };

  // Group headers are inserted into the flat list rather than nesting lists,
  // so infinite scroll stays a single scroll container.
  const withHeaders = [];
  let lastBucket = null;
  visible.forEach((n) => {
    const bucket = dayBucket(n.createdAt);
    if (bucket !== lastBucket) { withHeaders.push({ __header: bucket }); lastBucket = bucket; }
    withHeaders.push(n);
  });

  const panel = (
    <div
      style={{
        width: 'min(94vw, 440px)',
        background: token.colorBgElevated,
        borderRadius: token.borderRadiusLG,
        boxShadow: token.boxShadowSecondary,
        border: `1px solid ${token.colorBorderSecondary}`,
        overflow: 'hidden',
      }}
    >
      <div style={{
        padding: '10px 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
      >
        <Space size={8}>
          <Text strong>Notifications</Text>
          {/* Only shown when live updates are NOT working, so the user knows
              the list may be stale rather than silently trusting it. */}
          {streamStatus === 'degraded' && (
            <Tooltip title="Live updates are offline. The list refreshes when you open it.">
              <Text type="secondary" style={{ fontSize: 11 }}>offline</Text>
            </Tooltip>
          )}
        </Space>
        <Segmented
          size="small"
          value={filter}
          onChange={setFilter}
          options={[
            { label: 'All', value: 'all' },
            { label: unread ? `Unread (${unread})` : 'Unread', value: 'unread' },
          ]}
        />
      </div>

      <div
        style={{ maxHeight: 460, overflowY: 'auto' }}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (hasMore && !loading && el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMore();
        }}
      >
        {loading && !items.length ? (
          <div style={{ padding: 16 }}><Skeleton active paragraph={{ rows: 3 }} /></div>
        ) : (
          <List
            dataSource={withHeaders}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={filter === 'unread' ? 'Nothing unread' : 'No notifications yet'}
                  style={{ padding: '28px 0' }}
                />
              ),
            }}
            renderItem={(item) => {
              if (item.__header) {
                return (
                  <div
                    key={`h-${item.__header}`}
                    style={{
                      padding: '6px 14px',
                      background: token.colorFillQuaternary,
                      fontSize: 11,
                      letterSpacing: '.06em',
                      textTransform: 'uppercase',
                      color: token.colorTextTertiary,
                    }}
                  >
                    {item.__header}
                  </div>
                );
              }
              return (
                <List.Item
                  onClick={() => handleClick(item)}
                  style={{
                    padding: '11px 14px',
                    cursor: 'pointer',
                    background: item.read ? 'transparent' : token.colorPrimaryBg,
                    alignItems: 'flex-start',
                  }}
                  actions={[
                    !item.read && (
                      <Tooltip title="Mark read" key="r">
                        <Button
                          size="small" type="text" shape="circle" icon={<CheckOutlined />}
                          onClick={(e) => { e.stopPropagation(); markRead(item.id); }}
                        />
                      </Tooltip>
                    ),
                    <Tooltip title="Remove" key="d">
                      <Button
                        size="small" type="text" shape="circle" icon={<DeleteOutlined />}
                        onClick={(e) => { e.stopPropagation(); dismiss(item.id); }}
                      />
                    </Tooltip>,
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={(
                      <span style={{
                        display: 'inline-flex',
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `${severityColor(item.severity)}1A`,
                        marginTop: 2,
                      }}
                      >
                        <NotificationIcon type={item.type} severity={item.severity} />
                      </span>
                    )}
                    title={(
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <Text strong={!item.read} style={{ fontSize: 13.5 }}>{item.title}</Text>
                        <Tooltip title={new Date(item.createdAt).toLocaleString()}>
                          <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                            {relativeTime(item.createdAt)}
                          </Text>
                        </Tooltip>
                      </div>
                    )}
                    description={(
                      <Text type="secondary" style={{ fontSize: 12.5 }}>{item.message || '—'}</Text>
                    )}
                  />
                </List.Item>
              );
            }}
          />
        )}
        {loading && items.length > 0 && (
          <div style={{ padding: 10, textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 12 }}>Loading…</Text></div>
        )}
      </div>

      <div style={{
        padding: '8px 12px',
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      >
        <Space size={4}>
          <Button size="small" type="text" onClick={() => markAllRead()} disabled={!unread}>Mark all read</Button>
          <Popconfirm title="Clear all notifications?" okText="Clear" okButtonProps={{ danger: true }} onConfirm={dismissAll}>
            <Button size="small" type="text" danger disabled={!items.length}>Clear</Button>
          </Popconfirm>
        </Space>
        <Space size={4}>
          <Tooltip title="Notification settings">
            <Button
              size="small" type="text" icon={<SettingOutlined />}
              onClick={() => { onOpenChange(false); navigate('/notifications/settings'); }}
            />
          </Tooltip>
          <Button size="small" type="link" onClick={() => { onOpenChange(false); navigate('/notifications'); }}>
            See all
          </Button>
        </Space>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={onOpenChange}
      dropdownRender={() => panel}
      trigger={['click']}
      placement="bottomRight"
    >
      <div style={{ cursor: 'pointer', marginLeft: 12 }} role="button" tabIndex={0} aria-label="Notifications">
        <Badge count={unread} size="small" overflowCount={99}>
          <BellOutlined style={{ fontSize: 20, color: '#fff' }} />
        </Badge>
      </div>
    </Dropdown>
  );
};

export default NotificationBell;
